import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { ReactRenderer } from "@tiptap/react";
import Mention from "@tiptap/extension-mention";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { getColorForUserName } from "utils/helpers/getColorForUserName";

export interface MentionUser {
  id: string;
  name: string;
  email: string;
}

export interface MentionDocument {
  id: string;
  title: string;
}

export type MentionItem =
  | { type: "user"; data: MentionUser }
  | { type: "document"; data: MentionDocument };

interface MentionListProps extends SuggestionProps {
  items: MentionItem[];
}

interface MentionListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
      setSelectedIndex(0);
    }, [props.items]);

    const selectItem = (item: MentionItem) => {
      if (item.type === "user") {
        props.command({
          id: item.data.id,
          label: item.data.name,
          mentionType: "user"
        });
      } else {
        props.command({
          id: item.data.id,
          label: item.data.title,
          mentionType: "document"
        });
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          setSelectedIndex(
            (prev) => (prev + props.items.length - 1) % props.items.length
          );
          return true;
        }

        if (event.key === "ArrowDown") {
          setSelectedIndex((prev) => (prev + 1) % props.items.length);
          return true;
        }

        if (event.key === "Enter") {
          const item = props.items[selectedIndex];
          if (item) {
            selectItem(item);
          }
          return true;
        }

        return false;
      }
    }));

    if (props.items.length === 0) {
      return null;
    }

    const userItems = props.items.filter((i) => i.type === "user");
    const docItems = props.items.filter((i) => i.type === "document");

    let flatIndex = 0;

    return (
      <div className="mention-suggestion-dropdown">
        {userItems.length > 0 && docItems.length > 0 && (
          <div className="mention-section-header">People</div>
        )}
        {userItems.map((item) => {
          const idx = flatIndex++;
          return (
            <button
              key={item.data.id}
              className={`mention-suggestion-item ${
                idx === selectedIndex ? "is-selected" : ""
              }`}
              onClick={() => selectItem(item)}
            >
              <span className="mention-suggestion-name">{item.data.name}</span>
              <span className="mention-suggestion-email">
                {item.data.email}
              </span>
            </button>
          );
        })}
        {userItems.length > 0 && docItems.length > 0 && (
          <div className="mention-section-header">Documents</div>
        )}
        {docItems.map((item) => {
          const idx = flatIndex++;
          return (
            <button
              key={item.data.id}
              className={`mention-suggestion-item ${
                idx === selectedIndex ? "is-selected" : ""
              }`}
              onClick={() => selectItem(item)}
            >
              <span className="mention-suggestion-doc-icon">📄</span>
              <span className="mention-suggestion-name">{item.data.title}</span>
            </button>
          );
        })}
      </div>
    );
  }
);

MentionList.displayName = "MentionList";

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] ?? "").toUpperCase();
}

function isInAccountableColumn(dom: HTMLElement): boolean {
  const td = dom.closest("td");
  if (!td) return false;
  const tr = td.parentElement;
  if (!tr) return false;
  const colIndex = Array.from(tr.children).indexOf(td);
  const table = td.closest("table");
  if (!table) return false;
  const headerRow = table.querySelector("tr:first-child");
  if (!headerRow) return false;
  const th = headerRow.children[colIndex];
  if (!th) return false;
  // When the header has an icon, the text is stored in a child's title attribute
  const iconWrapper = th.querySelector("[title]");
  const headerText = (iconWrapper?.getAttribute("title") ?? th.textContent ?? "")
    .trim()
    .toLowerCase();
  return headerText === "accountable";
}

export const AvatarMention = Mention.extend({
  name: "mention",

  addNodeView() {
    return ({ node }) => {
      const label = node.attrs.label ?? "";
      const dom = document.createElement("span");
      dom.contentEditable = "false";
      dom.title = label;

      // Defer column check to after DOM insertion
      requestAnimationFrame(() => {
        if (isInAccountableColumn(dom)) {
          dom.className = "mention-avatar";
          dom.textContent = getInitials(label);
          dom.style.backgroundColor = getColorForUserName(label);
        } else {
          dom.className = "mention-text";
          dom.textContent = `@${label}`;
        }
      });

      return { dom };
    };
  }
});

export const DocumentMention = Mention.extend({
  name: "documentMention",

  addNodeView() {
    return ({ node }) => {
      const label = node.attrs.label ?? "";
      const dom = document.createElement("span");
      dom.className = "mention-document";
      dom.contentEditable = "false";
      dom.textContent = `@${label}`;
      dom.title = label;
      dom.addEventListener("click", () => {
        document.body.dispatchEvent(
          new CustomEvent("document-mention-click", {
            detail: { documentId: node.attrs.id }
          })
        );
      });
      return { dom };
    };
  }
});

export const createMentionSuggestionOptions = (
  users: MentionUser[],
  documents: MentionDocument[] = []
): Omit<SuggestionOptions<MentionItem>, "editor"> => ({
  items: ({ query }) => {
    if (query.length < 1) {
      return [];
    }

    const q = query.toLowerCase();

    const userItems: MentionItem[] = users
      .filter(
        (user) =>
          user.name.toLowerCase().startsWith(q) ||
          user.email.toLowerCase().startsWith(q)
      )
      .map((user) => ({ type: "user", data: user }));

    const docItems: MentionItem[] = documents
      .filter((doc) => doc.title.toLowerCase().startsWith(q))
      .map((doc) => ({ type: "document", data: doc }));

    return [...userItems, ...docItems];
  },

  command: ({ editor, range, props }) => {
    const nodeAfter = editor.view.state.selection.$to.nodeAfter;
    const overrideSpace = nodeAfter?.text?.startsWith(" ");

    if (overrideSpace) {
      range.to += 1;
    }

    const nodeType =
      props.mentionType === "document" ? "documentMention" : "mention";

    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        {
          type: nodeType,
          attrs: {
            id: props.id,
            label: props.label
          }
        },
        { type: "text", text: " " }
      ])
      .run();

    editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
  },

  render: () => {
    let renderer: ReactRenderer<MentionListHandle, MentionListProps>;
    let popup: HTMLDivElement | null = null;

    return {
      onStart: (props) => {
        renderer = new ReactRenderer(MentionList, {
          props,
          editor: props.editor
        });

        popup = document.createElement("div");
        popup.style.position = "absolute";
        popup.style.zIndex = "1000";
        popup.appendChild(renderer.element);
        document.body.appendChild(popup);

        if (props.clientRect) {
          const rect = props.clientRect();
          if (rect) {
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom + 4}px`;
          }
        }
      },

      onUpdate: (props) => {
        renderer?.updateProps(props);

        if (popup && props.clientRect) {
          const rect = props.clientRect();
          if (rect) {
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom + 4}px`;
          }
        }
      },

      onKeyDown: (props) => {
        if (props.event.key === "Escape") {
          popup?.remove();
          popup = null;
          renderer?.destroy();
          return true;
        }

        return renderer?.ref?.onKeyDown(props) ?? false;
      },

      onExit: () => {
        popup?.remove();
        popup = null;
        renderer?.destroy();
      }
    };
  }
});
